import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import axios from 'axios'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google'
import { X, Eye, EyeOff, Mail } from 'lucide-react'
import { api } from '../../../utils/variables'
import { google } from '../../../config'
import { getOrCreateDeviceId } from '../../../utils/storage'
import { storePendingVerificationRedirect } from '../../../utils/loginRedirect'
import { analytics, ANALYTICS_EVENTS } from '../../../analytics'
import googleIcon from '../../../assets/images/Frame.png'
import mainlogo from '../../../assets/images/new_web_logo.png'
import './AuthModal.css'

const UNVERIFIED_ERROR = 'User is not active. Please activate your account'

// Same localStorage shape the login saga / Login.js Google handler write, so
// the rest of the app (api_helper's guest check, Authmiddleware, etc.) sees
// a session from this modal exactly like one from the /login page.
const persistSession = (data, fallbackEmail) => {
  const email = data?.email || fallbackEmail || ''
  localStorage.setItem('authToken', data.access_token)
  if (email) {
    localStorage.setItem('userEmail', email)
    localStorage.setItem('userName', email.split('@')[0] || '')
  }
  localStorage.setItem('authUser', JSON.stringify({ data }))
}

const isRealUserSession = () => {
  if (!localStorage.getItem('authToken')) return false
  try {
    const authUser = JSON.parse(localStorage.getItem('authUser'))
    return !(authUser?.data?.is_guest === true || authUser?.is_guest === true)
  } catch (e) {
    return false
  }
}

const passwordRules = [
  { test: v => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: v => /[a-z]/.test(v), label: 'One lowercase letter' },
  { test: v => /\d/.test(v), label: 'One number' },
  { test: v => v.length >= 8, label: 'Minimum 8 characters' },
]

/**
 * Reusable in-place Sign In / Sign Up modal — for pages that want a visitor
 * to authenticate without leaving them (unlike /login and /register, which
 * navigate away). Deliberately has no "continue as guest" option: callers
 * use it where a real account is required (e.g. buying a plan).
 *
 * Email sign-up still needs a verification click before the account can log
 * in, so a successful sign-up switches to a "check your inbox" step instead
 * of calling onAuthSuccess. `returnPath` is parked for the verification tab
 * (see storePendingVerificationRedirect) so the link brings them back here.
 * If they sign in from another tab meanwhile, the storage listener below
 * picks that up and completes the flow in this tab too.
 */
const AuthModal = ({
  show,
  onHide,
  onAuthSuccess,
  defaultTab = 'signup',
  title,
  subtitle,
  returnPath,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [step, setStep] = useState('form') // form | verify
  const [pendingEmail, setPendingEmail] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [resending, setResending] = useState(false)

  const onAuthSuccessRef = useRef(onAuthSuccess)
  onAuthSuccessRef.current = onAuthSuccess

  const signInForm = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string().email('Please enter a valid email').required('Please enter your email'),
      password: Yup.string().required('Please enter your password'),
    }),
    onSubmit: async values => {
      setError('')
      setNotice('')
      setSubmitting(true)
      try {
        const res = await axios.post(`${api}/user/login`, { email: values.email, password: values.password })
        if (!res?.data?.access_token) throw new Error('Login failed. Please try again.')
        persistSession(res.data, values.email)
        analytics.setUser(getOrCreateDeviceId())
        analytics.trackEvent(ANALYTICS_EVENTS.AUTH.LOGIN, { method: 'email', source: 'auth_modal' })
        onAuthSuccessRef.current?.()
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Login failed. Please try again.'
        if (msg === UNVERIFIED_ERROR) {
          // Same as Login.js: resend the activation link rather than just erroring.
          try { await axios.post(`${api}/user/activate`, { email: values.email }) } catch (e) { /* ignore */ }
          setPendingEmail(values.email)
          setStep('verify')
          setNotice('Your account isn’t verified yet — we’ve sent a fresh verification link.')
        } else {
          setError(msg)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const signUpForm = useFormik({
    initialValues: { fullName: '', email: '', password: '', confirmPassword: '' },
    validationSchema: Yup.object({
      fullName: Yup.string().required('Please enter your full name'),
      email: Yup.string().email('Please enter a valid email').required('Please enter your email'),
      password: Yup.string()
        .required('Please enter a password')
        .matches(/[A-Z]/, 'Password must contain at least one capital letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .min(8, 'Password must be at least 8 characters long'),
      confirmPassword: Yup.string()
        .required('Please confirm your password')
        .oneOf([Yup.ref('password')], 'Passwords must match'),
    }),
    onSubmit: async values => {
      setError('')
      setNotice('')
      setSubmitting(true)
      try {
        await axios.post(`${api}/user/register`, {
          email: values.email,
          password: values.password,
          name: values.fullName,
        })
        analytics.trackEvent(ANALYTICS_EVENTS.AUTH.SIGN_UP, { method: 'email', source: 'auth_modal' })
        // Same keys Register.js writes — VerifyUser.js reads them back.
        try { localStorage.setItem('pendingVerificationEmail', values.email) } catch (e) { }
        try { localStorage.setItem('registeredEmail', values.email) } catch (e) { }
        try { localStorage.setItem('firstAttemptEmail', values.email) } catch (e) { }
        if (returnPath) storePendingVerificationRedirect(returnPath, values.email)
        signInForm.setFieldValue('email', values.email)
        setPendingEmail(values.email)
        setStep('verify')
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Registration failed. Please try again.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleGoogle = async response => {
    setError('')
    const idToken = response && (response.credential || response.tokenId)
    if (!idToken) {
      setError('Google sign-in failed. Please try again.')
      return
    }
    setSubmitting(true)
    try {
      const res = await axios.post(`${api}/user/google-login`, { id_token: idToken })
      if (!res?.data?.access_token) throw new Error('Failed to retrieve access token')
      persistSession(res.data)
      analytics.setUser(getOrCreateDeviceId())
      analytics.trackEvent(ANALYTICS_EVENTS.AUTH.LOGIN, { method: 'google', source: 'auth_modal' })
      onAuthSuccessRef.current?.()
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Google sign-in failed')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (!pendingEmail) return
    setResending(true)
    setError('')
    try {
      await axios.post(`${api}/user/activate`, { email: pendingEmail })
      setNotice('Verification link sent again — check your inbox (and spam folder).')
    } catch (e) {
      setError('Couldn’t resend the link. Please try again in a moment.')
    } finally {
      setResending(false)
    }
  }

  // Reset to a clean form every time the modal opens.
  useEffect(() => {
    if (!show) return
    setActiveTab(defaultTab)
    setStep('form')
    setError('')
    setNotice('')
    setShowPassword(false)
    signInForm.resetForm()
    signUpForm.resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, defaultTab])

  // A visitor who verifies their email in another tab and signs in there
  // should have this tab carry on too, instead of being stuck on "check
  // your inbox".
  useEffect(() => {
    if (!show) return
    const onStorage = e => {
      if (e.key !== 'authToken' && e.key !== 'authUser') return
      if (isRealUserSession()) onAuthSuccessRef.current?.()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [show])

  const switchTab = tab => {
    setActiveTab(tab)
    setError('')
    setNotice('')
    setShowPassword(false)
  }

  const fieldError = (form, name) => (form.touched[name] && form.errors[name] ? form.errors[name] : null)

  const renderInput = (form, name, label, { type = 'text', placeholder, autoComplete, password = false } = {}) => {
    const err = fieldError(form, name)
    return (
      <div className="kq-auth__field">
        <label className="kq-auth__label" htmlFor={`kq-auth-${name}`}>{label}</label>
        <div className="kq-auth__input-wrap">
          <input
            id={`kq-auth-${name}`}
            name={name}
            type={password ? (showPassword ? 'text' : 'password') : type}
            placeholder={placeholder}
            autoComplete={autoComplete}
            className={`kq-auth__input${password ? ' kq-auth__input--password' : ''}${err ? ' is-invalid' : ''}`}
            value={form.values[name]}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
          {password && (
            <button
              type="button"
              className="kq-auth__eye"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        </div>
        {err && <div className="kq-auth__error-text">{err}</div>}
      </div>
    )
  }

  const isSignUp = activeTab === 'signup'

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="kq-auth-modal"
      contentClassName="kq-auth-modal__content"
      aria-labelledby="kq-auth-title"
    >
      <div className="kq-auth">
        <button type="button" className="kq-auth__close" onClick={onHide} aria-label="Close">
          <X size={22} />
        </button>

        {step === 'verify' ? (
          <div className="kq-auth__verify">
            <div className="kq-auth__verify-icon"><Mail size={28} /></div>
            <h2 id="kq-auth-title" className="kq-auth__title">Check your inbox</h2>
            <p className="kq-auth__subtitle">
              We sent a verification link to <strong>{pendingEmail}</strong>. Verify your email, then sign in here to continue.
            </p>
            {notice && <div className="kq-auth__notice">{notice}</div>}
            {error && <div className="kq-auth__alert" role="alert">{error}</div>}
            <button
              type="button"
              className="kq-auth__submit"
              onClick={() => {
                signInForm.setFieldValue('email', pendingEmail)
                setStep('form')
                switchTab('signin')
              }}
            >
              I’ve verified — Sign in
            </button>
            <div className="kq-auth__verify-links">
              <button type="button" className="kq-auth__link" onClick={handleResend} disabled={resending}>
                {resending ? 'Sending…' : 'Resend link'}
              </button>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                className="kq-auth__link"
                onClick={() => {
                  setStep('form')
                  switchTab('signup')
                }}
              >
                Use a different email
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="kq-auth__header">
              <img src={mainlogo} alt="KeyQuest" className="kq-auth__logo" />
              <h2 id="kq-auth-title" className="kq-auth__title">
                {title || (isSignUp ? 'Create your account' : 'Welcome back!')}
              </h2>
              {subtitle && <p className="kq-auth__subtitle">{subtitle}</p>}
            </div>

            <div className="kq-auth__tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={!isSignUp}
                className={`kq-auth__tab${!isSignUp ? ' active' : ''}`}
                onClick={() => switchTab('signin')}
              >
                Sign In
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isSignUp}
                className={`kq-auth__tab${isSignUp ? ' active' : ''}`}
                onClick={() => switchTab('signup')}
              >
                Sign Up
              </button>
            </div>

            {notice && <div className="kq-auth__notice">{notice}</div>}
            {error && <div className="kq-auth__alert" role="alert">{error}</div>}

            {isSignUp ? (
              <form
                noValidate
                onSubmit={e => {
                  e.preventDefault()
                  signUpForm.handleSubmit()
                }}
              >
                {renderInput(signUpForm, 'fullName', 'Full Name', { placeholder: 'Enter your full name', autoComplete: 'name' })}
                {renderInput(signUpForm, 'email', 'Email Address', { type: 'email', placeholder: 'Enter your email', autoComplete: 'email' })}
                {renderInput(signUpForm, 'password', 'Password', { placeholder: 'Create a password', autoComplete: 'new-password', password: true })}
                {signUpForm.values.password && (
                  <ul className="kq-auth__rules">
                    {passwordRules.map(rule => {
                      const ok = rule.test(signUpForm.values.password)
                      return (
                        <li key={rule.label} className={ok ? 'ok' : ''}>
                          {ok ? '✓' : '•'} {rule.label}
                        </li>
                      )
                    })}
                  </ul>
                )}
                {renderInput(signUpForm, 'confirmPassword', 'Confirm Password', { placeholder: 'Re-enter your password', autoComplete: 'new-password', password: true })}
                <button type="submit" className="kq-auth__submit" disabled={submitting}>
                  {submitting ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Create account'}
                </button>
              </form>
            ) : (
              <form
                noValidate
                onSubmit={e => {
                  e.preventDefault()
                  signInForm.handleSubmit()
                }}
              >
                {renderInput(signInForm, 'email', 'Email Address', { type: 'email', placeholder: 'Enter your email', autoComplete: 'email' })}
                {renderInput(signInForm, 'password', 'Password', { placeholder: 'Enter your password', autoComplete: 'current-password', password: true })}
                <div className="kq-auth__row-end">
                  {/* New tab so the page that opened this modal isn't lost. */}
                  <a href="/forgot-password" target="_blank" rel="noopener noreferrer" className="kq-auth__link">
                    Forgot password?
                  </a>
                </div>
                <button type="submit" className="kq-auth__submit" disabled={submitting}>
                  {submitting ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Sign in'}
                </button>
              </form>
            )}

            <div className="kq-auth__separator"><span>Or continue with</span></div>

            <GoogleOAuthProvider clientId={google.CLIENT_ID || ''}>
              <div className="kq-auth__google-wrap">
                <button type="button" className="kq-auth__google" tabIndex={-1}>
                  <img src={googleIcon} alt="" />
                  <span>Google</span>
                </button>
                <div className="kq-auth__google-overlay">
                  <GoogleLogin
                    onSuccess={handleGoogle}
                    onError={() => setError('Google sign-in failed. Please try again.')}
                    useOneTap={false}
                  />
                </div>
              </div>
            </GoogleOAuthProvider>

            <p className="kq-auth__terms">
              By continuing, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}

export default AuthModal
